import { useEffect, useState } from "react";
import {
  createComparatorOffer,
  updateComparatorOffer,
  useAction,
} from "wasp/client/operations";
import type { ComparatorFirm, ComparatorOffer } from "wasp/entities";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../client/components/ui/dialog";
import { Button } from "../../../client/components/ui/button";
import { Input } from "../../../client/components/ui/input";
import { Label } from "../../../client/components/ui/label";
import { Checkbox } from "../../../client/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../client/components/ui/select";
import { useToast } from "../../../client/hooks/use-toast";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  offer: ComparatorOffer | null;
  firms: ComparatorFirm[];
  onSaved: () => void;
}

const NONE = "__none__";

// datetime-local wants "YYYY-MM-DDTHH:mm"; convert an ISO/Date to that local shape.
function toLocalInput(value: Date | string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

function platformsToInput(value: unknown): string {
  if (Array.isArray(value)) return (value as string[]).join(", ");
  return "";
}

export default function OfferFormDialog({ open, onOpenChange, offer, firms, onSaved }: Props) {
  const createOffer = useAction(createComparatorOffer);
  const updateOffer = useAction(updateComparatorOffer);
  const { toast } = useToast();

  const [firmId, setFirmId] = useState("");
  const [planName, setPlanName] = useState("");
  const [accountSizeK, setAccountSizeK] = useState("");
  const [accountType, setAccountType] = useState<"eval" | "direct">("eval");
  const [priceUsd, setPriceUsd] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");
  const [discountExpiresAt, setDiscountExpiresAt] = useState("");
  const [billingType, setBillingType] = useState<"one_time" | "monthly">("one_time");
  const [activationFeeUsd, setActivationFeeUsd] = useState("0");
  const [platforms, setPlatforms] = useState("");
  const [drawdownUsd, setDrawdownUsd] = useState("");
  const [drawdownType, setDrawdownType] = useState<string>(NONE);
  const [profitTargetUsd, setProfitTargetUsd] = useState("");
  const [dailyLossUsd, setDailyLossUsd] = useState("");
  const [roundTripMicroUsd, setRoundTripMicroUsd] = useState("");
  const [roundTripMiniUsd, setRoundTripMiniUsd] = useState("");
  const [maxContracts, setMaxContracts] = useState("");
  const [affiliateUrl, setAffiliateUrl] = useState("");
  const [isPublished, setIsPublished] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFirmId(offer?.firmId ?? firms[0]?.id ?? "");
    setPlanName(offer?.planName ?? "");
    setAccountSizeK(offer?.accountSizeK != null ? String(offer.accountSizeK) : "");
    setAccountType((offer?.accountType as "eval" | "direct") ?? "eval");
    setPriceUsd(offer?.priceUsd != null ? String(offer.priceUsd) : "");
    setDiscountPercent(offer?.discountPercent != null ? String(offer.discountPercent) : "");
    setDiscountExpiresAt(toLocalInput(offer?.discountExpiresAt));
    setBillingType((offer?.billingType as "one_time" | "monthly") ?? "one_time");
    setActivationFeeUsd(offer?.activationFeeUsd != null ? String(offer.activationFeeUsd) : "0");
    setPlatforms(platformsToInput(offer?.platforms));
    setDrawdownUsd(offer?.drawdownUsd != null ? String(offer.drawdownUsd) : "");
    setDrawdownType(offer?.drawdownType ?? NONE);
    setProfitTargetUsd(offer?.profitTargetUsd != null ? String(offer.profitTargetUsd) : "");
    setDailyLossUsd(offer?.dailyLossUsd != null ? String(offer.dailyLossUsd) : "");
    setRoundTripMicroUsd(
      offer?.roundTripMicroUsd != null ? String(offer.roundTripMicroUsd) : "",
    );
    setRoundTripMiniUsd(offer?.roundTripMiniUsd != null ? String(offer.roundTripMiniUsd) : "");
    setMaxContracts(offer?.maxContracts != null ? String(offer.maxContracts) : "");
    setAffiliateUrl(offer?.affiliateUrl ?? "");
    setIsPublished(offer?.isPublished ?? true);
  }, [offer, open, firms]);

  const numOrUndef = (v: string) => (v.trim() ? Number(v) : undefined);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        firmId,
        planName,
        accountSizeK: Number(accountSizeK),
        accountType,
        priceUsd: Number(priceUsd),
        discountPercent: numOrUndef(discountPercent),
        discountExpiresAt: discountExpiresAt.trim()
          ? new Date(discountExpiresAt).toISOString()
          : "",
        billingType,
        activationFeeUsd: activationFeeUsd.trim() ? Number(activationFeeUsd) : 0,
        platforms: platforms
          .split(/[,\s]+/)
          .map((p) => p.trim())
          .filter(Boolean),
        drawdownUsd: numOrUndef(drawdownUsd),
        drawdownType:
          drawdownType !== NONE
            ? (drawdownType as "eod" | "eot" | "trailing" | "static")
            : undefined,
        profitTargetUsd: numOrUndef(profitTargetUsd),
        dailyLossUsd: numOrUndef(dailyLossUsd),
        roundTripMicroUsd: numOrUndef(roundTripMicroUsd),
        roundTripMiniUsd: numOrUndef(roundTripMiniUsd),
        maxContracts: numOrUndef(maxContracts),
        affiliateUrl: affiliateUrl.trim() ? affiliateUrl.trim() : undefined,
        isPublished,
      };
      if (offer) {
        await updateOffer({ id: offer.id, ...payload });
      } else {
        await createOffer(payload);
      }
      toast({ title: "Saved" });
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast({ title: (e as Error).message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{offer ? "Edit offer" : "New offer"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Firm</Label>
              <Select value={firmId} onValueChange={setFirmId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select firm" />
                </SelectTrigger>
                <SelectContent>
                  {firms.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="offer-plan">Plan name</Label>
              <Input
                id="offer-plan"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="offer-size">Account size (K)</Label>
              <Input
                id="offer-size"
                type="number"
                value={accountSizeK}
                onChange={(e) => setAccountSizeK(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Account type</Label>
              <Select
                value={accountType}
                onValueChange={(v) => setAccountType(v as "eval" | "direct")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="eval">eval</SelectItem>
                  <SelectItem value="direct">direct</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="offer-price">Price (USD)</Label>
              <Input
                id="offer-price"
                type="number"
                value={priceUsd}
                onChange={(e) => setPriceUsd(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="offer-discount">Discount (%)</Label>
              <Input
                id="offer-discount"
                type="number"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="offer-discount-exp">Discount expires</Label>
              <Input
                id="offer-discount-exp"
                type="datetime-local"
                value={discountExpiresAt}
                onChange={(e) => setDiscountExpiresAt(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Billing</Label>
              <Select
                value={billingType}
                onValueChange={(v) => setBillingType(v as "one_time" | "monthly")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">one_time</SelectItem>
                  <SelectItem value="monthly">monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="offer-activation">Activation fee (USD)</Label>
              <Input
                id="offer-activation"
                type="number"
                value={activationFeeUsd}
                onChange={(e) => setActivationFeeUsd(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="offer-platforms">Platforms (comma or space)</Label>
              <Input
                id="offer-platforms"
                value={platforms}
                onChange={(e) => setPlatforms(e.target.value)}
                placeholder="NinjaTrader, Tradovate"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="offer-drawdown">Drawdown (USD)</Label>
              <Input
                id="offer-drawdown"
                type="number"
                value={drawdownUsd}
                onChange={(e) => setDrawdownUsd(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Drawdown type</Label>
              <Select value={drawdownType} onValueChange={setDrawdownType}>
                <SelectTrigger>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>—</SelectItem>
                  <SelectItem value="eod">eod</SelectItem>
                  <SelectItem value="eot">eot</SelectItem>
                  <SelectItem value="trailing">trailing</SelectItem>
                  <SelectItem value="static">static</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="offer-target">Profit target (USD)</Label>
              <Input
                id="offer-target"
                type="number"
                value={profitTargetUsd}
                onChange={(e) => setProfitTargetUsd(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="offer-daily">Daily loss (USD)</Label>
              <Input
                id="offer-daily"
                type="number"
                value={dailyLossUsd}
                onChange={(e) => setDailyLossUsd(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="offer-maxcontracts">Max contracts</Label>
              <Input
                id="offer-maxcontracts"
                type="number"
                value={maxContracts}
                onChange={(e) => setMaxContracts(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="offer-rt-micro">Round trip micro (USD)</Label>
              <Input
                id="offer-rt-micro"
                type="number"
                value={roundTripMicroUsd}
                onChange={(e) => setRoundTripMicroUsd(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="offer-rt-mini">Round trip mini (USD)</Label>
              <Input
                id="offer-rt-mini"
                type="number"
                value={roundTripMiniUsd}
                onChange={(e) => setRoundTripMiniUsd(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="offer-affiliate">Affiliate URL</Label>
            <Input
              id="offer-affiliate"
              value={affiliateUrl}
              onChange={(e) => setAffiliateUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="offer-published"
              checked={isPublished}
              onCheckedChange={(v) => setIsPublished(v === true)}
            />
            <Label htmlFor="offer-published">Published</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
